import React, { useState } from 'react';

import { useFormik } from 'formik';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';

import { addDataLayerEntry } from '@printdeal/data-layer-manager';

import {
  Pagination,
  Heading,
  Empty,
  Link,
  TextField,
  Spinner,
} from '@printdeal/ui-library';

import Breadcrumb from '../../../../Breadcrumb';
import ComplaintOrderSearchOrder from './ComplaintOrderSearchOrder';
import { useOrders } from '../../hooks/useOrders';
import { messages } from '../../../translations/complaint';
import Head from '../../../../PrivateHead';

const ORDERS_PER_PAGE = 7;

const registerEvent = (eventAction: string, extraData: any | undefined = undefined) => {
  addDataLayerEntry('complaint_order_search', {
    action: eventAction,
    extraData: JSON.stringify(extraData),
  });
};

const ComplaintOrderSearchPage = () => {
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const intl = useIntl();

  const {
    status,
    orders,
    total,
    isRefetching,
  } = useOrders({
    ordersPerPage: ORDERS_PER_PAGE,
    pageIndex,
    search,
    filter: 'shipped',
  });

  const searchFormik = useFormik({
    initialValues: {
      orderSearch: '',
    },
    onSubmit: async ({ orderSearch }) => {
      setSearch(orderSearch);
      setPageIndex(1);
      registerEvent('use_search');
    },
  });

  const flipPage = (pageNumber: number) => {
    setPageIndex(pageNumber);
    registerEvent('use_pagination', { pageNumber });
  };

  return (
    <div className="data-sentry-block">
      <Head title={intl.formatMessage(messages.addComplaintTitle)} />
      <div className="pd-flex pd-flex-col pd-gap-6">
        <Breadcrumb>
          <Breadcrumb.Item to="../..">
            <FormattedMessage id="account.index.title" defaultMessage="My account" />
          </Breadcrumb.Item>
          <Breadcrumb.Item to="..">
            <FormattedMessage id="complaints.title" defaultMessage="My Complaints" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>
            <FormattedMessage id="complaints.add.title" defaultMessage="File complaint" />
          </Breadcrumb.Item>
        </Breadcrumb>

        <Heading>
          <FormattedMessage id="complaints.add.title" defaultMessage="File complaint" />
        </Heading>

        <Heading as="h2">
          <FormattedMessage id="complaints.add.step1" defaultMessage="1. Choose the order the complaint is about" />
        </Heading>

        <div className="pd-max-w-60">
          <form onSubmit={searchFormik.handleSubmit}>
            <TextField
              id="orderSearch"
              type="text"
              placeholder={intl.formatMessage(messages.searchForAnOrder)}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...searchFormik.getFieldProps('orderSearch')}
            />
          </form>
        </div>

        {(status === 'pending' || isRefetching) && (
          <Spinner
            variant="dim"
            screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            className="pd-my-4"
          />
        )}

        {(status === 'success') && (
          <>
            <ul data-cy="orderList" className="pd-flex pd-flex-col pd-gap-4">
              {orders?.map((order) => (
                <li key={order.id}>
                  <ComplaintOrderSearchOrder order={order} />
                </li>
              ))}
            </ul>

            <Pagination total={total as number} currentPage={pageIndex} aria-label={intl.formatMessage({ id: 'general.pagination.label', defaultMessage: 'Pagination' })} perPage={ORDERS_PER_PAGE} siblingPages={3} className="pd-mt-12 pd-justify-center">
              {({ pages, totalPages }) => (
                <>
                  <Pagination.Button as="button" variant="prev" onClick={flipPage}>
                    <FormattedMessage id="general.pagination.button.previous" defaultMessage="Previous" />
                  </Pagination.Button>

                  <Pagination.Items className="pd-hidden sm:pd-flex">
                    {pages.map((pageNumber) => <Pagination.Item as="button" key={pageNumber} onClick={flipPage} />)}
                  </Pagination.Items>

                  <Pagination.Label className="sm:pd-hidden">
                    <FormattedMessage
                      id="general.pagination.mobileText"
                      defaultMessage="Page {pageNumber} of {totalPages}"
                      values={{
                        pageNumber: pageIndex,
                        totalPages,
                      }}
                    />
                  </Pagination.Label>

                  <Pagination.Button as="button" variant="next" onClick={flipPage}>
                    <FormattedMessage id="general.pagination.button.Next" defaultMessage="Next" />
                  </Pagination.Button>
                </>
              )}
            </Pagination>
          </>
        )}

        {status === 'success' && total === 0 && (
          <Empty
            title={intl.formatMessage({
              id: 'account.orders.noOrders',
              defaultMessage: 'Aww, sadly we cannot find an order! :(',
            })}
            description={(
              <FormattedMessage
                id="account.orders.complaints.noOrders.description"
                defaultMessage="No valid orders could be found for creating complaints."
              />
            )}
          />
        )}

        <Link
          as={GatsbyLink}
          to=".."
          variant="secondary"
          onClick={() => registerEvent('click_my_account_link')}
          iconBefore="chevron-left"
        >
          <FormattedMessage id="complaints.backToComplaints" defaultMessage="Back to complaints" />
        </Link>
      </div>
    </div>
  );
};

export default ComplaintOrderSearchPage;
